/*
 * THE BILLING PAGE — a tab inside Clerk's organisation profile, in EVERY Vitesse app.
 *
 * Billing belongs where an admin already goes to manage the org, not on a page of our own that
 * nobody thinks to look for.
 *
 * It lives here, wired into the org-switcher MOUNT, for the same reason the permissions page does:
 * an app asks for an org switcher, not for an org switcher plus billing plus permissions. This
 * glue was originally ~150 lines inside ai-ui, and copying it into fleet-ui and app-ui is exactly
 * how three billing pages start showing three different prices. One copy, three mounts.
 *
 * THE FETCH LIVES HERE, NOT IN THE PANEL (Cardinal Rule). Clerk owns the modal's lifecycle and
 * mounts on tab open; a component that fetched on connect would fire a request every time somebody
 * clicked around a dialog we do not control.
 */
import { t } from './i18n';
import { live } from './ai/live';
import { LiveTopic } from '@vitesse/proto/vitesse/ai/v1/live_pb.js';
import './elements/vitesse-icon';
/**
 * STRIPE.JS, LOADED ONLY WHEN SOMEBODY BUYS.
 *
 * Not on boot: a third-party script on every pageview for a button most sessions never press.
 * Module-scoped and memoised, because two <script> tags means two Stripe globals — and being
 * module-scoped rather than per-page means switching apps does not re-load it either.
 */
let stripeJs;
function loadStripeJs() {
    stripeJs ??= new Promise((resolve, reject) => {
        const el = document.createElement('script');
        el.src = 'https://js.stripe.com/v3';
        el.onload = () => resolve(window.Stripe);
        el.onerror = () => {
            stripeJs = undefined; // a failed load is not cached — the next click retries
            reject(new Error('stripe.js failed to load'));
        };
        document.head.appendChild(el);
    });
    return stripeJs;
}
/*
 * PREWARMED "ADD A CARD". The slow part of adding a card is not the click — it is what the click
 * kicks off: load Stripe.js, POST for a SetupIntent, construct the Stripe instance. Done on the
 * click, the form takes a beat to appear. So we do ALL of it the moment the Payment view opens,
 * while the user's eyes are still travelling to the button, and cache the result. The click then
 * only mounts the already-fetched Element — instant. A SetupIntent that ends up unused simply
 * expires server-side; that is the cheap price of the instant open.
 */
let cardPrewarm;
function prewarmAddCard(pubKey, opts) {
    cardPrewarm ??= (async () => {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/payment-method/setup`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        });
        const body = (await res.json());
        if (!res.ok || !body.clientSecret)
            throw new Error(body.error ?? String(res.status));
        const Stripe = await loadStripeJs();
        return { stripe: Stripe(pubKey), clientSecret: body.clientSecret };
    })();
    // A failed prewarm is not cached — the click retries from scratch rather than inheriting the error.
    cardPrewarm.catch(() => {
        cardPrewarm = undefined;
    });
    return cardPrewarm;
}
/*
 * WHERE STRIPE MOUNTS. The panel renders an empty `.billing-checkout-host` in whichever view
 * opened a checkout; the page appends Stripe's mount node INTO it. Empty in the template so Lit
 * never touches the children this page adds. `checkoutOpen` must be set (and the render flushed)
 * before the host exists to query.
 */
async function openCheckoutHost(el) {
    el.checkoutOpen = true;
    await el.updateComplete;
    const host = el.querySelector('.billing-checkout-host');
    if (!host)
        throw new Error('checkout host missing');
    return host;
}
let warm;
/*
 * The ACTIVE VIEW survives the remount too. The identity provider re-mounts this page once
 * shortly after the dialog opens (its own org fetch resolving re-renders the profile), and a
 * remount that reset the pills to "plan" threw the customer out of the view they had chosen —
 * most visibly out of a payment form they were mid-way through. Data comes back warm; so does
 * the place they were standing.
 */
let warmView = 'plan';
/** One authenticated call, one moment where the numbers agree with each other. Plan, credits and
 *  storage fetched separately can render a tier the org no longer has beside a balance it does. */
/**
 * Fill the warm cache BEFORE anyone opens the tab.
 *
 * `/api/billing/summary` is six independent reads behind one endpoint, so the
 * first visit paid for all of them while the panel showed its loading state —
 * and it is the tab an admin opens from a cold dialog, so it was slow exactly
 * when it was most visible. Every later visit was instant off `warm`, which
 * meant the fix was not caching (that existed) but that nothing primed it.
 *
 * Mirrors what the RBAC pages already do at boot. Deliberately fire-and-forget
 * and deliberately unawaited: a failure leaves the cache empty and the tab
 * behaves exactly as it did before — the warm is an optimisation, and an
 * optimisation that can keep the page from opening is not one.
 *
 * Once per page load, guarded on `warm` so re-opening the dialog does not
 * re-issue the six reads.
 */
export function warmBilling(opts) {
    if (warm)
        return;
    // A detached panel is the load's own target — loadBilling writes onto an
    // element, and reusing it here means the warm path and the visit path cannot
    // drift into fetching different things.
    const el = document.createElement('vitesse-billing-panel');
    void loadBilling(el, opts).catch(() => { });
}
async function loadBilling(el, opts, fresh = false) {
    try {
        const tok = await opts.getToken();
        /*
         * `fresh` asks the worker to skip its cached card list. It is set only after this panel has
         * itself added or removed a card — the cache is per-isolate on the worker side and cannot be
         * invalidated across isolates, so the client is what knows a read must not be cached.
         */
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/summary${fresh ? '?fresh=1' : ''}`, {
            headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (!res.ok)
            throw new Error(String(res.status));
        const s = (await res.json());
        // A section the server could not read comes back absent, and stays absent here: the panel
        // hides what it has no data for. `?? undefined` rather than a default, because a default is a
        // claim — "no plan", "no credit", "nothing stored" — and each of those is a worse answer than
        // showing nothing at all.
        el.plan = s.plan ?? undefined;
        // Merged onto the plan rather than fetched by the panel: the panel takes data, it does not go
        // looking for it. A host that supplies nothing leaves every row showing its allowance alone.
        if (el.plan && opts.usedCounts) {
            try {
                el.plan = { ...el.plan, used: await opts.usedCounts() };
            }
            catch {
                /* a count we could not read is absent, which the row already handles */
            }
        }
        el.credits = s.credits ?? undefined;
        el.catalog = s.catalog ?? undefined;
        // If the admin is already sitting on the Payment view when credits (and the publishable key)
        // land, warm the add-a-card path now rather than waiting for their click.
        if (el.view === 'payment' && el.credits?.publishableKey)
            void prewarmAddCard(el.credits.publishableKey, opts).catch(() => { });
        warm = { ...warm, plan: el.plan, credits: el.credits, catalog: el.catalog };
        // Partial is not failure. The old behaviour raised the same generic banner for "one read was
        // slow" and "nothing loaded", which is why a missing card list read as a broken billing page.
        el.error = s.plan || s.credits ? '' : t('billing.loadFailed');
    }
    catch {
        // A number nobody can read is better than a blank panel, which reads as "you are storing
        // nothing" — the one thing a storage page must never imply by accident.
        el.error = t('billing.loadFailed');
    }
}
/**
 * The storage figures, fetched AFTER the panel has painted.
 *
 * They are the slow half — every table holding the org's content, plus the whole blob ledger — and
 * the plan, balance and cards were waiting behind them. This is deliberately not awaited by the
 * caller: the panel renders its own "loading" line for this section, so a slow scan delays one block
 * rather than the page.
 *
 * A failure here leaves the section absent rather than raising the page-level banner. "One number is
 * missing" and "nothing loaded" are different things, and only the second should look broken.
 */
/** A burst of uploads is one read, not one per upload. Long enough to swallow a multi-file drop,
 *  short enough that the figure still reads as live. */
const STORAGE_REFRESH_COALESCE_MS = 1_500;
async function loadStorage(el, opts) {
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/storage`, {
            headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (!res.ok)
            return;
        const s = (await res.json());
        // Replace only with data. A null answer keeps whatever was showing — during a live-driven
        // refetch, "the read failed" must not repaint as "you store nothing".
        if (s.storage) {
            el.usage = s.storage;
            warm = { ...warm, usage: el.usage };
        }
    }
    catch {
        /* the section stays on its loading line; the plan and balance above it are unaffected */
    }
}
/*
 * BUY CREDITS — embedded, so the customer never leaves the dialog they are standing in.
 *
 * The panel only says which pack was clicked; everything with money or a token in it happens here.
 * Stripe mounts into a LIGHT-dom node slotted into the panel: Stripe.js mounts an iframe into an
 * element it is handed, and one inside our shadow root is not reachable from the page.
 *
 * CREDIT IS NOT GRANTED HERE. The webhook does that, and it is the only path that touches the
 * ledger — so a customer who closes the tab mid-payment still gets what they paid for, and a forged
 * return proves nothing. On completion we simply re-read the summary.
 */
async function buyCredits(el, asked, pubKey, opts) {
    el.buying = true;
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/checkout`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
            body: JSON.stringify(asked),
        });
        const body = (await res.json());
        if (!res.ok || !body.clientSecret)
            throw new Error(body.error ?? String(res.status));
        const Stripe = await loadStripeJs();
        const mount = document.createElement('div');
        (await openCheckoutHost(el)).appendChild(mount);
        const checkout = await Stripe(pubKey).initEmbeddedCheckout({
            clientSecret: body.clientSecret,
            onComplete: () => {
                // Paid. The balance arrives via the webhook, which may land a beat later than this
                // callback — so re-read rather than adding the pack locally and hoping.
                el.checkoutOpen = false;
                mount.remove();
                void loadBilling(el, opts);
            },
        });
        checkout.mount(mount);
        el.addEventListener('vitesse-billing-cancel', () => {
            checkout.destroy();
            mount.remove();
            el.checkoutOpen = false;
        }, { once: true });
    }
    catch {
        el.error = t('billing.buyFailed');
    }
    finally {
        el.buying = false;
    }
}
/*
 * CHANGE TIER — a subscription Checkout, embedded, in the dialog the customer is already in.
 *
 * Structurally identical to buying credits and deliberately so: one embedded-checkout path, one set
 * of failure modes. The ENTITLEMENT IS NOT GRANTED HERE. Stripe's webhook moves the org's tier, the
 * plans worker signs the new grant, and fleet-control projects it — so a customer who closes the tab
 * mid-payment still gets what they paid for, and a forged return proves nothing.
 */
/**
 * CANCEL THE PLAN — return to the floor at period end.
 *
 * No Stripe form because nothing is charged: cancelling is a server-side subscription update, and
 * the tier only actually moves when the webhook fires at period end. So this posts, then re-reads —
 * the summary is what says which tier the org is on, and a cancellation scheduled for later must
 * not make the page claim the org is already on Free.
 */
async function cancelPlan(el, opts) {
    el.changingTier = true;
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/cancel`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        });
        if (!res.ok) {
            el.error = (await res.json().catch(() => ({}))).error ?? t('billing.cancelPlanFailed');
            return;
        }
        await loadBilling(el, opts, true);
    }
    catch {
        el.error = t('billing.cancelPlanFailed');
    }
    finally {
        el.changingTier = false;
    }
}
async function changeTier(el, tierId, pubKey, opts) {
    el.changingTier = true;
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/subscribe`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
            body: JSON.stringify({ tierId }),
        });
        const body = (await res.json());
        if (!res.ok)
            throw new Error(body.error ?? String(res.status));
        /*
         * A TIER CHANGE ON A LIVE SUBSCRIPTION FINISHES HERE, with no checkout to mount.
         *
         * An org that already subscribes is moving tier, not buying a second plan, so the price is
         * swapped on the subscription it already has. That needs no payment collection — the card is
         * already on file — so there is nothing for Stripe.js to render.
         *
         * Tested BEFORE the missing-secret failure rather than after: this reply legitimately carries
         * no client secret, and the old single check read exactly that as "Stripe gave us nothing" and
         * would show a failure to a customer whose upgrade had just succeeded.
         */
        if (body.changed) {
            await loadBilling(el, opts, true);
            return;
        }
        if (!body.clientSecret)
            throw new Error(body.error ?? String(res.status));
        const Stripe = await loadStripeJs();
        const mount = document.createElement('div');
        (await openCheckoutHost(el)).appendChild(mount);
        const checkout = await Stripe(pubKey).initEmbeddedCheckout({
            clientSecret: body.clientSecret,
            onComplete: () => {
                // Paid. The tier arrives via the webhook → grant → projection chain, which lands a beat
                // after this callback, so re-read rather than showing the new tier optimistically and
                // being wrong if any link failed.
                el.checkoutOpen = false;
                mount.remove();
                void loadBilling(el, opts);
            },
        });
        checkout.mount(mount);
        el.addEventListener('vitesse-billing-cancel', () => {
            checkout.destroy();
            mount.remove();
            el.checkoutOpen = false;
        }, { once: true });
    }
    catch {
        el.error = t('billing.tierChangeFailed');
    }
    finally {
        el.changingTier = false;
    }
}
/**
 * Save an auto top-up setting.
 *
 * On success it RE-READS rather than trusting the local edit: the ledger normalises what it stored,
 * and showing the customer what they typed instead of what was saved is how a UI ends up disagreeing
 * with the thing that will actually charge their card.
 */
/**
 * Set or clear the monthly AI spending budget.
 *
 * `clear` returns to the plan's default rather than removing the ceiling — those are different
 * outcomes, and the server keeps them distinct, so the client must not collapse them into one call.
 */
async function saveBudget(el, detail, opts) {
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/budget`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
            body: JSON.stringify(detail),
        });
        const body = (await res.json().catch(() => ({})));
        // The server's words on refusal: it knows the bounds, and restating them here would be a second
        // copy of a rule that can drift from the one being enforced.
        if (!res.ok)
            el.budgetError = body.error ?? t('billing.budgetSaveFailed');
    }
    catch {
        el.budgetError = t('billing.budgetSaveFailed');
    }
    // Re-read either way, so the control shows what is stored rather than what was typed.
    await loadBilling(el, opts);
}
async function saveAutoTopup(el, cfg, opts) {
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/autotopup`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
            body: JSON.stringify(cfg),
        });
        const body = (await res.json().catch(() => ({})));
        if (!res.ok) {
            // The server's own words: it explains the loop the numbers would cause, which is the only
            // thing that tells the customer how to fix them.
            el.autoTopupError = body.error ?? t('billing.autoTopupSaveFailed');
            // Re-read anyway, so the controls snap back to what is actually stored rather than sitting on
            // a rejected value that looks saved.
            await loadBilling(el, opts);
            return;
        }
        el.autoTopupError = '';
        await loadBilling(el, opts);
    }
    catch {
        el.autoTopupError = t('billing.autoTopupSaveFailed');
    }
}
/*
 * ADD A CARD — an embedded Checkout Session in `setup` mode.
 *
 * Structurally identical to buying credits and to changing tier, deliberately: one embedded path,
 * one set of failure modes, one place where Stripe.js gets mounted into a light-DOM slot.
 *
 * NOTHING IS CHARGED. What comes back is a saved payment method, which is what auto top-up needs in
 * order to charge a customer who is not sitting here.
 */
/**
 * The appearance the Payment Element renders with, read from the page it is standing on.
 *
 * This is the whole reason for the Element over embedded Checkout. Checkout draws in an iframe whose
 * colours come from the Stripe account's branding settings, so it arrived looking like Stripe inside
 * a page that looks like us and no stylesheet of ours could reach it. The Element takes these
 * values, so the fields inherit the app's own tokens.
 *
 * Resolved from computed style rather than hardcoded, so the form follows a theme switch instead of
 * matching whichever theme happened to be open when someone wrote the hex down. Each falls back to
 * the token's own default, because a missing variable must degrade to Stripe's default rather than
 * to an empty string, which the Element rejects.
 */
function elementAppearance(host) {
    const css = getComputedStyle(host);
    const v = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
    /*
     * THE NAMES HERE MUST BE THE ONES IN tokens.css.
     *
     * The first version of this read --vs-accent, --vs-surface and friends, which exist nowhere: every
     * lookup missed and every fallback won, so a function written to follow the theme returned the
     * same hardcoded palette in both. It typechecked, it rendered, and it was decorative. A token read
     * by name is only as good as the name.
     */
    const bg = v('--bg-primary', '#ffffff');
    const dark = isDark(bg);
    return {
        // The page's own background decides which of Stripe's two base themes starts closer, so the
        // variables below adjust it rather than fight it.
        theme: dark ? 'night' : 'stripe',
        variables: {
            colorPrimary: v('--blue', '#1a8cd8'),
            colorBackground: bg,
            colorText: v('--text-primary', dark ? '#ffffff' : '#1a1a1a'),
            colorTextSecondary: v('--text-secondary', '#9ca3af'),
            colorDanger: v('--red', '#e03e42'),
            fontFamily: v('--font', 'system-ui, sans-serif'),
            borderRadius: v('--radius-md', '12px'),
            spacingUnit: '4px',
        },
    };
}
/** Whether a CSS colour reads as dark, so the Element starts from the nearer base theme. Handles the
 *  hex and rgb() forms a computed style actually returns; anything else is treated as light, which is
 *  Stripe's own default and therefore the safe guess. */
function isDark(color) {
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    let r = 255;
    let g = 255;
    let b = 255;
    if (hex) {
        const h = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
        r = parseInt(h.slice(0, 2), 16);
        g = parseInt(h.slice(2, 4), 16);
        b = parseInt(h.slice(4, 6), 16);
    }
    else {
        const m = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i.exec(color);
        if (!m)
            return false;
        [r, g, b] = [Number(m[1]), Number(m[2]), Number(m[3])];
    }
    // Rec. 601 luma, which is the usual "is this light or dark" test and is more than precise enough
    // for choosing between two base themes.
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
/**
 * SAVE A CARD, in a form that belongs to this page.
 *
 * A SetupIntent confirmed by the Payment Element, rather than an embedded Checkout session. Same
 * outcome — a card on file that auto top-up can charge — and the difference is entirely in what the
 * customer sees: the Element is ours to style, and Checkout was not.
 *
 * `redirect: 'if_required'` keeps an ordinary card in the dialog. A card that demands 3-D Secure
 * still redirects, which is the bank's decision and not ours to refuse; the return URL is this page,
 * so it comes back to where it started.
 */
async function addCard(el, pubKey, opts) {
    el.addingCard = true;
    try {
        // Consume the prewarm the Payment view kicked off (Stripe.js + SetupIntent + Stripe instance
        // already in flight, usually done); if the user clicked before it ran, this starts it now.
        const { stripe, clientSecret } = await prewarmAddCard(pubKey, opts);
        cardPrewarm = undefined; // consumed — the next Add-a-card warms a fresh SetupIntent
        const mount = document.createElement('div');
        (await openCheckoutHost(el)).appendChild(mount);
        const elements = stripe.elements({ clientSecret, appearance: elementAppearance(el) });
        const payment = elements.create('payment');
        payment.mount(mount);
        // The Element has no button of its own — unlike Checkout, submitting is ours to offer. It lands
        // in the panel's actions row, to the RIGHT of Cancel, so Save and Cancel read as one row of
        // decisions rather than a button floating inside the Stripe iframe.
        const submit = document.createElement('button');
        submit.type = 'button';
        submit.className = 'base-button base-button-sm base-button-blue';
        submit.textContent = t('billing.saveCard');
        const actions = el.querySelector('.billing-checkout-actions');
        actions?.appendChild(submit);
        const teardown = () => {
            payment.destroy();
            mount.remove();
            submit.remove();
            el.checkoutOpen = false;
        };
        submit.addEventListener('click', () => {
            submit.disabled = true;
            submit.textContent = t('billing.savingCard');
            void stripe
                .confirmSetup({ elements, confirmParams: { return_url: location.href }, redirect: 'if_required' })
                .then((r) => {
                if (r.error) {
                    // Stripe's words: it knows why the bank refused, and a generic message here would send
                    // the customer to try the same card again.
                    el.error = r.error.message ?? t('billing.addCardFailed');
                    submit.disabled = false;
                    submit.textContent = t('billing.saveCard');
                    return;
                }
                teardown();
                // Re-read rather than adding the card locally: the summary is what says which card is
                // DEFAULT, and that is the question the list exists to answer. Fresh, because the card was
                // created seconds ago and a cached list would not have it.
                void loadBilling(el, opts, true);
            });
        });
        el.addEventListener('vitesse-billing-cancel', teardown, { once: true });
    }
    catch {
        el.error = t('billing.addCardFailed');
    }
    finally {
        el.addingCard = false;
    }
}
/** Remove a saved card. The server checks the card belongs to this org — the panel only names it. */
async function removeCard(el, paymentMethodId, opts) {
    try {
        const tok = await opts.getToken();
        const res = await fetch(`${opts.apiBase ?? ''}/api/billing/payment-method/remove`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
            body: JSON.stringify({ paymentMethodId }),
        });
        if (!res.ok) {
            el.error = (await res.json().catch(() => ({}))).error ?? t('billing.removeCardFailed');
        }
    }
    catch {
        el.error = t('billing.removeCardFailed');
    }
    // Re-read either way: the list must reflect what the server holds, not what we hoped happened.
    // Fresh, because this panel is what changed it and a cached list would still show the removed card.
    await loadBilling(el, opts, true);
}
export function billingPage(opts) {
    let panel;
    let unliveStorage;
    let onVisible;
    return {
        label: t('billing.title'),
        url: 'billing',
        mountIcon: (el) => {
            el.innerHTML =
                '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>';
        },
        unmountIcon: () => { },
        mount: (el) => {
            // Clerk can call mount again on a container that still holds the previous mount's children
            // (observed: a second pill bar stacked above a reset panel). Idempotent by construction:
            // whatever is there is this page's own output, and this mount replaces it whole.
            el.replaceChildren();
            panel = document.createElement('vitesse-billing-panel');
            /*
             * THE VIEW PICKER — the SAME underlined tab strip the Members screen carries (`.base-tab`),
             * one concern per view: the plan (always-expanded comparison), payment methods, AI credits,
             * storage. Beneath it, the same one-line explainer slot those pages reserve, so the content
             * below starts at the same y on every tab.
             *
             * Built here in the page, not inside the panel: the page hosts the picker and tells the
             * panel which view was chosen — the same division as everything else on this file, where
             * the panel renders and the page wires. Switching views TEARS DOWN an open Stripe form:
             * left mounted, the iframe floated over whichever view came next.
             */
            const VIEWS = [
                { id: 'plan', label: t('billing.viewPlan'), hint: t('billing.viewPlanHint') },
                { id: 'payment', label: t('billing.viewPayment'), hint: t('billing.viewPaymentHint') },
                { id: 'credits', label: t('billing.viewCredits'), hint: t('billing.viewCreditsHint') },
                { id: 'storage', label: t('billing.viewStorage'), hint: t('billing.viewStorageHint') },
            ];
            const tabs = document.createElement('div');
            tabs.className = 'base-tab-list';
            tabs.setAttribute('role', 'tablist');
            const hint = document.createElement('p');
            hint.className = 'base-field-hint org-page-hint';
            const paint = () => {
                for (const b of tabs.querySelectorAll('button'))
                    b.setAttribute('aria-selected', b.dataset.view === warmView ? 'true' : 'false');
                hint.textContent = VIEWS.find((v) => v.id === warmView)?.hint ?? '';
            };
            // Ready the add-a-card path: the full prewarm once the publishable key is known, else at least
            // the Stripe script. Safe to call more than once — both underlying warms are memoised.
            const warmPayment = () => {
                const pk = panel?.credits?.publishableKey;
                if (pk)
                    void prewarmAddCard(pk, opts).catch(() => { });
                else
                    void loadStripeJs().catch(() => { });
            };
            for (const v of VIEWS) {
                const b = document.createElement('button');
                b.className = 'base-tab';
                b.setAttribute('role', 'tab');
                b.dataset.view = v.id;
                b.textContent = v.label;
                b.addEventListener('click', () => {
                    if (warmView !== v.id && panel?.checkoutOpen) {
                        panel.dispatchEvent(new CustomEvent('vitesse-billing-cancel', { bubbles: true, composed: true }));
                    }
                    warmView = v.id;
                    panel.view = v.id;
                    // Opening the Payment view is a strong signal a card is about to be added, so warm the
                    // WHOLE add-a-card path now (Stripe.js + SetupIntent + Stripe instance) — with the
                    // publishable key if credits are loaded, else at least the script. It readies while the
                    // user's eyes travel to the button, so the click is instant. Still not on boot: only once
                    // someone actually looks at Payment.
                    if (v.id === 'payment')
                        warmPayment();
                    paint();
                });
                tabs.appendChild(b);
            }
            panel.view = warmView;
            if (warmView === 'payment')
                warmPayment();
            paint();
            el.appendChild(tabs);
            el.appendChild(hint);
            el.appendChild(panel);
            /*
             * PAINT FROM THE WARM ENTRY BEFORE ANY FETCH. A revisit shows the last known numbers in the
             * same frame the tab opens; the loads below then replace them with newer ones. This is the
             * whole of the "slow and staticky" fix: the network is now a background concern, and a
             * loading state only ever appears on the genuinely-first visit.
             */
            if (warm) {
                panel.plan = warm.plan;
                panel.credits = warm.credits;
                panel.catalog = warm.catalog;
                panel.usage = warm.usage;
            }
            // The publishable key is CLOUD-FETCHED with the rest of the summary, never a build-time
            // constant (no-env-vars.md): sandbox→live is then a worker config change, not a rebuild.
            panel.addEventListener('vitesse-billing-buy', (e) => {
                // Either a pack id or a custom amount — passed through verbatim so the SERVER decides what
                // is allowed. Validating here would put the bounds in two places, and the copy the customer
                // sees would drift from the copy that is enforced.
                const asked = e.detail;
                const pk = panel?.credits?.publishableKey;
                if (!pk) {
                    panel.error = t('billing.buyFailed');
                    return;
                }
                void buyCredits(panel, asked, pk, opts);
            });
            // A tier change starts a Stripe subscription Checkout, the same embedded flow as a credit
            // pack — the customer never leaves the dialog. The panel says which tier was clicked; the
            // money happens here.
            panel.addEventListener('vitesse-billing-tier', (e) => {
                const tierId = e.detail.tierId;
                const pk = panel?.credits?.publishableKey;
                if (!pk) {
                    panel.error = t('billing.tierChangeFailed');
                    return;
                }
                void changeTier(panel, tierId, pk, opts);
            });
            // Cancel the plan — no Stripe form, just a POST. The panel already confirmed; the server
            // schedules the cancellation at period end and the webhook drops the tier when the period
            // closes, so we re-read rather than move the tier optimistically.
            panel.addEventListener('vitesse-billing-cancel-plan', () => void cancelPlan(panel, opts));
            // Auto top-up. The panel does not validate — the ledger owns the rule that a top-up must
            // exceed its threshold, and the customer sees that refusal rather than a second copy of it.
            // ADD A CARD — the same embedded machinery as buying credits, in `setup` mode so nothing is
            // charged. Reusing that path rather than a second one means one set of failure modes.
            panel.addEventListener('vitesse-billing-add-card', () => {
                const pk = panel?.credits?.publishableKey;
                if (!pk) {
                    panel.error = t('billing.addCardFailed');
                    return;
                }
                void addCard(panel, pk, opts);
            });
            panel.addEventListener('vitesse-billing-remove-card', (e) => {
                const id = e.detail.paymentMethodId;
                void removeCard(panel, id, opts);
            });
            panel.addEventListener('vitesse-billing-autotopup', (e) => {
                void saveAutoTopup(panel, e.detail, opts);
            });
            panel.addEventListener('save-budget', (e) => {
                void saveBudget(panel, e.detail, opts);
            });
            // Re-read after any change the panel made through its own controls (tier change, auto-topup)
            // rather than trusting a local edit: the worker is the only thing that knows what actually
            // took effect.
            panel.addEventListener('vitesse-billing-changed', () => void loadBilling(panel, opts));
            /*
             * STORED BYTES MOVE WITHOUT THIS TAB DOING ANYTHING.
             *
             * A colleague uploading a file changes what this org is billed, so a panel that loaded once
             * shows a figure that was true when the dialog opened. The worker announces the change on the
             * org's live channel and this refetches — rather than a timer, which would ask a question
             * nobody had an answer to for all the seconds nothing happened.
             */
            /*
             * COALESCED, because this read is expensive and the event is not.
             *
             * The storage figures mean summing the length of every row the org owns; the index narrows the
             * scan to that org, not to a small number. Wiring the event straight to a refetch made a burst
             * of uploads N scans, multiplied again by every panel open in the org — a live number that
             * costs more the more people are watching it is a live number that takes the database down.
             *
             * So a burst collapses into one read, and a hidden tab does none: the panel sits inside a
             * dialog that can be left open behind other work for hours, and refreshing a figure nobody is
             * looking at is the whole cost with none of the benefit. Reading resumes on visibility, which
             * is also when a stale number would first be seen.
             */
            let pending;
            let missed = false;
            const refreshStorage = () => {
                if (document.hidden) {
                    missed = true;
                    return;
                }
                if (pending)
                    return;
                pending = setTimeout(() => {
                    pending = undefined;
                    void loadStorage(panel, opts);
                }, STORAGE_REFRESH_COALESCE_MS);
            };
            onVisible = () => {
                if (!document.hidden && missed) {
                    missed = false;
                    refreshStorage();
                }
            };
            document.addEventListener('visibilitychange', onVisible);
            unliveStorage = live.subscribe(LiveTopic.STORAGE, refreshStorage);
            void loadBilling(panel, opts);
            void loadStorage(panel, opts);
        },
        unmount: () => {
            // Before the element goes: a subscription outliving its panel would refetch into a detached
            // node forever, which is a leak that only shows up as unexplained traffic.
            unliveStorage?.();
            unliveStorage = undefined;
            if (onVisible)
                document.removeEventListener('visibilitychange', onVisible);
            onVisible = undefined;
            panel?.remove();
            panel = undefined;
        },
    };
}
//# sourceMappingURL=billing-page.js.map